import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
  useRoutes,
} from "react-router-dom";
import { useEffect, type ReactNode } from "react";

const sentryDsn = process.env.REACT_APP_SENTRY_DSN?.trim();
const isProduction = process.env.REACT_APP_ENV === "production";
const tracesSampleRate = isProduction ? 0.2 : 1.0;

const tracePropagationTargets = [
  "localhost",
  /^https:\/\/api\.chronox\.ai/,
  /^https:\/\/api-dev\.chronox\.ai/,
  /^https:\/\/api\.chronox\.in/,
  /^https:\/\/app\.chronox\.ai/,
  /^https:\/\/app-dev\.chronox\.ai/,
  /^https:\/\/app\.chronox\.in/,
];

export const isSentryEnabled = Boolean(sentryDsn);

export const useSentryRoutes = isSentryEnabled
  ? Sentry.wrapUseRoutesV6(useRoutes)
  : useRoutes;

export function initSentry(): void {
  if (!sentryDsn) {
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate,
    tracePropagationTargets,
    enableLogs: true,
  });
}

type SentryProviderProps = {
  children: ReactNode;
};

export function SentryProvider({ children }: SentryProviderProps) {
  if (!isSentryEnabled) {
    return <>{children}</>;
  }

  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
